import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

const EMPTY_DATA = {
  platforms: [],
  metrics: [],
  schemas: [],
  entities: [],
  entitySnapshots: [],
  syncRuns: [],
  platformStats: null,
  membersData: null,
};

const MEMBER_DATA_TIMESTAMP_FIELDS = ["created_at", "last_login", "tier_updated_at", "status_updated_at", "updated_at"];

export function latestMemberDataTimestamp(records) {
  return (records || [])
    .flatMap((member) => MEMBER_DATA_TIMESTAMP_FIELDS.map((field) => member[field]))
    .filter((value) => value && Number.isFinite(new Date(value).getTime()))
    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] || null;
}

export function usePlatformData({ limit = 50, refreshMs = 30000 } = {}) {
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const activeRequest = useRef(null);

  const reload = useCallback(() => {
    if (activeRequest.current) return activeRequest.current;
    const request = (async () => {
    try {
      const results = await Promise.allSettled([
        api.get("/api/platform-data/platforms"),
        api.get("/api/platform-data/metrics", { params: { limit } }),
        api.get("/api/platform-data/schemas"),
        api.get("/api/platform-data/entities", { params: { limit } }),
        api.get("/api/platform-data/entity-snapshots", { params: { limit } }),
        api.get("/api/platform-data/sync-runs"),
        api.get("/api/platform-stats/"),
        api.get("/api/admin/members/stats"),
        api.get("/api/admin/members/list", { params: { limit: 500 } }),
      ]);
      const names = ["platforms", "metrics", "schemas", "entities", "snapshots", "sync runs", "platform stats", "member stats", "member list"];
      const failed = results.flatMap((result, index) => result.status === "rejected" ? [names[index]] : []);
      setData((previous) => {
        const next = { ...previous };
        [["platforms", "platforms"], ["metrics", "metrics"], ["schemas", "schemas"],
          ["entities", "entities"], ["entitySnapshots", "snapshots"], ["syncRuns", "sync_runs"]]
          .forEach(([key, responseKey], index) => {
            if (results[index].status === "fulfilled") next[key] = results[index].value.data?.[responseKey] || [];
          });
        if (results[6].status === "fulfilled") {
          const stats = results[6].value.data;
          next.platformStats = Array.isArray(stats) ? stats : null;
        }
        const statsLoaded = results[7].status === "fulfilled";
        const listLoaded = results[8].status === "fulfilled";
        if (statsLoaded || listLoaded) {
          const stats = statsLoaded ? results[7].value.data : previous.membersData?.stats;
          const list = listLoaded ? results[8].value.data : null;
          next.membersData = {
            stats,
            records: listLoaded ? list?.members || [] : previous.membersData?.records || [],
            total: list?.total ?? stats?.total ?? previous.membersData?.total ?? null,
          };
        }
        return next;
      });
      setError(failed.length ? `Could not refresh ${failed.join(", ")}. Available data is still shown; previously loaded sections may be stale.` : "");
      return failed.length === 0;
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Platform data is unavailable.");
      return false;
    } finally {
      setLoading(false);
      activeRequest.current = null;
    }
    })();
    activeRequest.current = request;
    return request;
  }, [limit]);

  useEffect(() => {
    let active = true;
    let refreshQueued = false;
    let timer;
    let failures = 0;
    const load = async () => {
      if (!active) return;
      window.clearTimeout(timer);
      if (activeRequest.current) {
        refreshQueued = true;
        await activeRequest.current;
        if (!active) return;
        if (refreshQueued) {
          refreshQueued = false;
          timer = window.setTimeout(load, 0);
        }
        return;
      }
      const success = await reload();
      if (!active) return;
      failures = success ? 0 : failures + 1;
      if (refreshQueued) {
        refreshQueued = false;
        timer = window.setTimeout(load, 0);
        return;
      }
      if (failures >= 3) return;
      timer = window.setTimeout(load, success ? refreshMs : 1000 * (2 ** (failures - 1)));
    };
    const onDataChanged = (event) => {
      if (!event?.detail?.resourceType || event.detail.resourceType === "*" || event.detail.resourceType === "platform-data") {
        load();
      }
    };
    load();
    window.addEventListener("gio:data-changed", onDataChanged);
    return () => {
      active = false;
      window.clearTimeout(timer);
      window.removeEventListener("gio:data-changed", onDataChanged);
    };
  }, [reload, refreshMs]);

  return { ...data, loading, error, reload };
}

export function latestMetricsByAccount(metrics) {
  const latest = new Map();
  for (const metric of metrics || []) {
    const key = [metric.platform, metric.account_id, metric.metric_key].join("\u0000");
    const current = latest.get(key);
    if (!current || String(metric.captured_at || "") > String(current.captured_at || "")) latest.set(key, metric);
  }
  return Array.from(latest.values());
}

export function metricDisplayValue(metric) {
  if (metric?.parsed_value === null || metric?.parsed_value === undefined) return metric?.raw_value || "Not available";
  if (typeof metric.parsed_value === "number") {
    const value = metric.parsed_value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return metric.currency ? `${metric.currency} ${value}` : `${value}${metric.unit || ""}`;
  }
  return String(metric.parsed_value);
}
